<template>
    <label class="base-label filter-label" v-if="label">{{label}}</label>
    <div class="filter-container" :class="{px0, 'ungrouped-sort': sortingActive,'tags-active': anyTagsAreActive}">
        <FilterInput class="filter-field" v-model="searchString"
            :placeholder="placeholder"
            @clear="searchString=''"
            @focus="()=>{$emit('focus')}"
            @blur="()=>{$emit('blur')}"
        />
        <div class="controls">
            <OutlineButton class="filter-tags-button" :text="$t('form.filter.tags.btn')"
                @click="openTagPicker"
                :highlighted="anyTagsAreActive"
                iconComponent="FilterIcon"
            />
            <OutlineButton class="sort-sales-button" :text="sortButtonLabel"
                @click="nextSortingMode"
                :highlighted="sortingActive"
                :vFlip="orderby === 'sales-asc'"
                iconComponent="SortIcon"
            />
            <OutlineButton class="multiselect-button" :text="$t('form.filter.multiSelect.btn')"
                v-if="enableMultiSelect"
                @click="openMultiSelectForm"
                dark 
            />
            <OutlineButton class="multiselect-button" :text="$t('form.filter.multiSelect.selectAll')"
                v-if="enableSelectAll"
                @click="()=>{$emit('select-all')}"
            />
        </div>
        <ActiveTags v-if="anyTagsAreActive" class="active-tags" :tags="taglist"/>
    </div>
</template>

<script>
 import * as types from '@/store/mutations'
 import OutlineButton from '@/components/core/OutlineButton.vue'
 import FilterInput from '@/components/forms/filter/FilterInput.vue'
 import ActiveTags from '@/components/forms/filter/ActiveTags.vue'

 export default {
     name: 'FormFilter',
     data(){ return {
         searchString: '',
         orderby: null // null | sales-desc | sales-asc
     }},
     props: {
         placeholder: String,
         label: { type: String, default: null },
         px0: Boolean,
         enableMultiSelect: Boolean,
         enableSelectAll: Boolean,
     },
     components: {
         ActiveTags,
         FilterInput,
         OutlineButton
     },
     watch: {
         searchString: { immediate: true, handler: function(value) {
             this.$emit('update-search-string', value)
         }},
         values: { immediate: true, handler: function(value) {
             this.$emit('update', value)
         }}
     },
     emits: ['update', 'update-search-string', 'focus', 'blur', 'select-all'],
     computed: {
         sortButtonLabel(){
             if(this.enableMultiSelect || this.enableSelectAll){
                 if(this.orderby === 'sales-asc'){
                     return this.$t('form.filter.order.short.salesAsc')
                 }
                 if(this.orderby === 'sales-desc'){
                     return this.$t('form.filter.order.short.salesDesc') 
                 }
                 return this.$t('form.filter.order.short.alphabetic')
             }

             // No third button, can use more space
             if(this.orderby === 'sales-asc'){
                 return this.$t('form.filter.order.long.salesAsc')
             }
             if(this.orderby === 'sales-desc'){
                 return this.$t('form.filter.order.long.salesDesc') 
             }
             return this.$t('form.filter.order.long.alphabetic')
         },
         searchResultCount(){
             let total = 0
             if(Array.isArray(this.values)){
                 return this.values.length 
             }
             for(let arr of this.values){
                 total += arr[1].length
             }
             return total
         },
         sortingActive(){
             return !!this.orderby
         },
         taglist () {
             let all = this.$store.getters.tags
             let activeTags = this.$store.state.customers.filters.activeTags
             
             let builtins = activeTags.builtin.reduce((list, id) => {
                 list[id] = all.builtin[id] || `TAG:${id}`
                 return list
             },{})

             let customs = activeTags.custom.reduce((list, id) => {
                 list[id] = all.custom[id] || `TAG:${id}`
                 return list
             },{})

             let combinedTagList = {
                 ...customs,
                 ...builtins
             }

             return combinedTagList
         },
         searchResults () {
             return this.$store.getters.getCustomersAlphabetic(this.searchString)
         },
         anyTagsAreActive(){
             let { builtin, custom } = this.$store.state.customers.filters.activeTags
             if(builtin.length || custom.length){
                 return true
             }
             return false
         },
         values () {
             // filter tags locally to avoid triggering slow getCustomersAlphabetic
             // disqualify entire tag groups (builtin, custom) if that tag array is empty
             // cache builtin tag lookups to avoid recursive arr.includes()

             let searchResults = this.searchResults
             let results = new Map()

             let builtinTags = this.$store.state.customers.filters.activeTags.builtin
             let hasBuiltinTags = builtinTags.length !== 0
             let checkForVip = false
             let checkForDealer = false
             let checkForHost = false
             let checkForCustomer = false
             let checkForFromProfile = false
             if(hasBuiltinTags){
                 checkForVip = builtinTags.includes('vip')
                 checkForDealer = builtinTags.includes('dealer')
                 checkForHost = builtinTags.includes('host')
                 checkForCustomer = builtinTags.includes('customer')
                 checkForFromProfile = builtinTags.includes('profile')
             }

             //console.log("Filter.vue : values: ", builtinTags);
             
             let customTags = new Map()
             let hasCustomTags = this.$store.state.customers.filters.activeTags.custom.length !== 0
             if(hasCustomTags){
                 for(let tag of this.$store.state.customers.filters.activeTags.custom){
                     customTags.set(tag, true)
                 }
             }

             const conditionsCount = builtinTags.length + customTags.size
             if(!conditionsCount){
                 results = searchResults
             }

             if(conditionsCount){
                 for(let [letter, letterGroup] of searchResults){
                     for(let customer of letterGroup){
                         let remainingConditions = conditionsCount
                         
                         // parse builtin tags
                         if(hasBuiltinTags){
                             if(checkForVip && customer.isVIP){
                                 remainingConditions--
                                 if(!remainingConditions){
                                     this.addCustomerToFilteredResults(customer, letter, results)
                                     continue
                                 }
                             }
                             if(checkForDealer && customer.isDealer){
                                 remainingConditions--
                                 if(!remainingConditions){
                                     this.addCustomerToFilteredResults(customer, letter, results)
                                     continue
                                 }
                             }
                             if(checkForHost && customer.isHost){
                                 remainingConditions--
                                 if(!remainingConditions){
                                     this.addCustomerToFilteredResults(customer, letter, results)
                                     continue
                                 }
                             }
                             if(checkForFromProfile && customer.isFromProfile){
                                 remainingConditions--
                                 if(!remainingConditions){
                                     this.addCustomerToFilteredResults(customer, letter, results)
                                     continue
                                 }
                             }
                             if(checkForCustomer && customer.isCustomer){
                                 remainingConditions--
                                 if(!remainingConditions){
                                     this.addCustomerToFilteredResults(customer, letter, results)
                                     continue
                                 }
                             }
                         }
                         
                         // parse custom tags
                         if(hasCustomTags){
                             let customerCustomTags = Object.values(customer.tags).map(tag => tag.id)
                             for(let [tag, value] of customTags){
                                 if(value && customerCustomTags.includes(tag)){
                                     remainingConditions--
                                     if(!remainingConditions){
                                         this.addCustomerToFilteredResults(customer, letter, results)
                                         continue
                                     }
                                 }
                             }
                         }
                         
                     }
                 }
             }

             if(!this.orderby){
                 return results
             }
             if(!results.size){
                 return []
             }

             let arr = []

             for(let group of results){
                 group[1].forEach(cust =>{ arr.push(cust) })
             }

             if(this.orderby === 'sales-desc'){
                 arr.sort((a, b) => {
                     return b['sort-by-sale'] - a['sort-by-sale'] || `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
                 })
             }

             if(this.orderby === 'sales-asc'){
                 arr.sort((a, b) => {
                     return a['sort-by-sale'] - b['sort-by-sale'] || `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
                 })
             }

             return arr
         }
     },
     methods: {
         addCustomerToFilteredResults(customer, group, map){
             if (!map.has(group)) {
                 map.set(group, [])
             }
             map.get(group).push(customer)
         },
         openTagPicker(){
             this.$store.commit(types.OPEN_FILTERPAGE)
         },
         openMultiSelectForm(){
             this.$store.commit(types.OPEN_SMSFORM)
         },
         nextSortingMode(){
             let newOrder = null
             if(this.orderby === null){
                 newOrder = 'sales-desc'
             }
             if(this.orderby === 'sales-desc'){
                 newOrder = 'sales-asc'
             }
             if(this.orderby === 'sales-asc'){
                 newOrder = null
             }
             this.orderby = newOrder
         }
     }
 }
</script>

<style lang="css" scoped>
.debug-tags {
    margin-top: 40px;
    margin-bottom: 40px;
    margin-left: -3px;
    margin-right: -3px;
}
.debug-tags > div {
    margin: 6px;
}

.filter-label {
    margin-bottom: -20px;
}

.filter-container {
    text-align: left;
    padding: 0px 10px 10px 10px;
  
}
.filter-container.px0 {
    padding-left: 0;
    padding-right: 0;
}
.filter-container.ungrouped-sort {
    padding-bottom: 20px;
}
.filter-container.ungrouped-sort.tags-active {
    padding-bottom: 40px;
}

.filter-field {
    margin-bottom: 20px;
}
.controls {
    display: flex;
    margin-bottom: 16px;
    gap: 6px;
    justify-content: flex-start;
}

.select-all-button,
.multiselect-button {
    margin-left: auto;
}
</style>
